import React, { useEffect, useState, useMemo } from 'react';

import withNavigation from "../higher-order/navigation.js";
import Translator from '../translator.js';
import OuiNonToggle from '../fermer-enregistrement-capture/oui-non-toggle.js';
import SecteurSection from './secteur-section.js';
import CaptureVerificationSommaire from '../fermer-enregistrement-capture/capture-verification-sommaire.js';
import FermerEnregistrementFormBase from '../fermer-enregistrement/shared/fermer-enregistrement-form-base.js';
import { useFermerEnregistrementForm } from '../fermer-enregistrement/shared/use-fermer-enregistrement-form.js';

import { useSelector } from "react-redux";

import { PATH_FERMER_ENREGISTREMENT } from '../../constants/navigation-constants.js';
import { getContexteFermetureChasse } from '../../server/services/fermer-enregistrement-service.js';
import {
  createEmptySecteurBlock,
  buildCaptureDetailsFromGibiers,
  getSecteursDisponibles,
  formEstComplete,
  afficherAutreSecteur,
  shouldRenderSecteurBlock,
  hasMoreSecteursDisponibles,
  estComplete,
  pruneOrphanSecteurBlocks,
  buildFermetureChasseCapturePayload,
  appliquerAutreGibierOui,
  appliquerAutreGibierNon,
  appliquerChangementGibierActif,
  verrouillerGibierActifSiComplete,
  obtenirGibierActifInitial,
  estQuestionAutreGibierResolue,
} from '../../utils/fermer-enregistrement-chasse-capture.js';

import '../fermer-enregistrement-capture/style.css';

const FermerEnregistrementChasseCapture = ({ goTo, location, language }) => {
  const { fermerEnregistrement: { idEnregistrementEntete } } = useSelector(state => state);
  const TEXTS = useMemo(() => Translator(language, 'TXT_FERMER_ENREGISTREMENT_PAGE'), [language]);

  const [dataLoading, setDataLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [aChasse, setAChasse] = useState(null);
  const [secteurs, setSecteurs] = useState([]);
  const [allSecteurs, setAllSecteurs] = useState([]);
  const [gibiersActivite, setGibiersActivite] = useState([]);
  const [idActiviteChasse, setIdActiviteChasse] = useState('');
  const [idEnregistrementDetailChasse, setIdEnregistrementDetailChasse] = useState('');

  const {
    calendarRef,
    date,
    load,
    heureIsSlected,
    dateIsSelected,
    setdateIsSelected,
    isOpenCalendar,
    setIsOpenCalendar,
    enregistrementEntete,
    dateErrorKey,
    minSelectableDate,
    maxSelectableDate,
    authToken,
    handleDateChange,
    handleTimeChange,
    submitFermeture,
  } = useFermerEnregistrementForm({
    location,
    idEnregistrementEntete,
    language,
  });

  useEffect(() => {
    if (!enregistrementEntete) {
      setDataLoading(false);
      return;
    }

    let cancelled = false;

    const loadData = async () => {
      setDataLoading(true);
      setLoadError('');
      try {
        const contexte = await getContexteFermetureChasse(enregistrementEntete);

        if (cancelled) {
          return;
        }

        setIdActiviteChasse(contexte.idActiviteChasse || '');
        setIdEnregistrementDetailChasse(contexte.idEnregistrementDetailChasse || '');
        setAllSecteurs(contexte.secteurs);
        setGibiersActivite(contexte.gibiers);
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          setLoadError(TEXTS.loadDataError);
        }
      } finally {
        if (!cancelled) {
          setDataLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [enregistrementEntete, TEXTS.loadDataError]);

  const updateSecteurs = (updater) => {
    setSecteurs(prev => pruneOrphanSecteurBlocks(updater(prev), allSecteurs));
  };

  const handleAChasseChange = (value) => {
    setAChasse(value);
    if (value === true) {
      setSecteurs([createEmptySecteurBlock()]);
    } else {
      setSecteurs([]);
    }
  };

  const handleSecteurChange = (index, idSecteur) => {
    const captureDetailsTemplate = buildCaptureDetailsFromGibiers(gibiersActivite);

    updateSecteurs(prev => {
      const updated = prev.slice(0, index + 1).map((block, i) => {
        if (i !== index) {
          return block;
        }
        return {
          ...block,
          idSecteur,
          nombrePersonnes: '',
          nombreJours: '',
          autreSecteur: null,
          autreEspece: null,
          especeRounds: [],
          activeEspeceId: '',
          aEuDesPrises: null,
          capturesDetails: captureDetailsTemplate.map(d => ({ ...d })),
        };
      });
      return updated;
    });
  };

  const handleNombrePersonnesChange = (index, value) => {
    updateSecteurs(prev => {
      const updated = prev.map((block, i) => {
        if (i !== index) {
          return block;
        }
        const nombreJours = Number(value) > 0 ? block.nombreJours : '';
        const countsComplete = Number(value) > 0 && Number(nombreJours) > 0;
        return {
          ...block,
          nombrePersonnes: value,
          nombreJours,
          ...(!countsComplete ? {
            autreSecteur: null,
            autreEspece: null,
            especeRounds: [],
            activeEspeceId: '',
            aEuDesPrises: null,
            capturesDetails: [],
          } : {}),
        };
      });
      if (!estComplete(updated[index])) {
        return updated.slice(0, index + 1);
      }
      return updated;
    });
  };

  const handleNombreJoursChange = (index, value) => {
    updateSecteurs(prev => {
      const updated = prev.map((block, i) => {
        if (i !== index) {
          return block;
        }
        const countsComplete = Number(block.nombrePersonnes) > 0 && Number(value) > 0;
        return {
          ...block,
          nombreJours: value,
          ...(!countsComplete ? {
            autreSecteur: null,
            autreEspece: null,
            especeRounds: [],
            activeEspeceId: '',
            aEuDesPrises: null,
            capturesDetails: [],
          } : {}),
        };
      });
      if (!estComplete(updated[index])) {
        return updated.slice(0, index + 1);
      }
      return updated;
    });
  };

  const handlePrisesChange = (blockIndex, value) => {
    let captureDetailsTemplate = [];
    const block = secteurs[blockIndex];

    if (value === true) {
      if (block.capturesDetails?.length > 0) {
        captureDetailsTemplate = block.capturesDetails;
      } else {
        captureDetailsTemplate = buildCaptureDetailsFromGibiers(gibiersActivite);
      }
    } else if (value === false) {
      // Aucune prise : on garde quand même un détail à 0 pour chaque gibier.
      captureDetailsTemplate = buildCaptureDetailsFromGibiers(gibiersActivite);
    }

    updateSecteurs(prev => prev.map((blockItem, i) => {
      if (i !== blockIndex) {
        return blockItem;
      }
      return {
        ...blockItem,
        aEuDesPrises: value,
        autreEspece: null,
        autreSecteur: null,
        especeRounds: [],
        activeEspeceId: value === true
          ? obtenirGibierActifInitial(captureDetailsTemplate)
          : '',
        capturesDetails: (value === true || value === false)
          ? captureDetailsTemplate.map(d => ({ ...d }))
          : [],
      };
    }));
  };

  const handleCaptureDetailChange = (blockIndex, detailIndex, field, value, extraUpdates = null) => {
    updateSecteurs(prev => prev.map((block, i) => {
      if (i !== blockIndex) {
        return block;
      }
      const updatedDetails = block.capturesDetails.map((d, di) => {
        if (di !== detailIndex) {
          return d;
        }
        return {
          ...d,
          [field]: value,
          ...(extraUpdates || {}),
        };
      });
      return verrouillerGibierActifSiComplete({ ...block, capturesDetails: updatedDetails });
    }));
  };

  const handleGibierChange = (index, gibierId) => {
    updateSecteurs(prev => prev.map((block, i) => {
      if (i !== index) {
        return block;
      }
      return appliquerChangementGibierActif(block, gibierId);
    }));
  };

  const handleAutreGibierChange = (index, value, roundIndex) => {
    updateSecteurs(prev => prev.map((block, i) => {
      if (i !== index) {
        return block;
      }
      if (value === true) {
        return appliquerAutreGibierOui(block, roundIndex);
      }
      return appliquerAutreGibierNon(block, roundIndex);
    }));
  };

  const handleAutreSecteurChange = (index, value) => {
    updateSecteurs(prev => {
      const block = prev[index];
      if (value === true && !estQuestionAutreGibierResolue(block)) {
        return prev;
      }
      if (value === true) {
        if (!hasMoreSecteursDisponibles(allSecteurs, prev)) {
          return prev.map((block, i) =>
            i === index ? { ...block, autreSecteur: false } : block
          );
        }
        const updated = prev.map((block, i) =>
          i === index ? { ...block, autreSecteur: value } : block
        );
        return [...updated, createEmptySecteurBlock()];
      }

      return prev.slice(0, index + 1).map((block, i) =>
        i === index ? { ...block, autreSecteur: false } : block
      );
    });
  };

  const navigation = async () => {
    if (authToken) {
      const result = await submitFermeture((decodedToken, dateFermetureISO) =>
        buildFermetureChasseCapturePayload({
          idProfile: decodedToken.idProfile,
          dateFermetureISO,
          idEnregistrementEntete: enregistrementEntete,
          idEnregistrementDetailChasse,
          aChasse,
          secteurs,
        })
      );
      if (result) {
        goTo(PATH_FERMER_ENREGISTREMENT);
      }
    }
  };

  const showHuntingQuestion = dateIsSelected && heureIsSlected && !dataLoading;
  const showSubmit = formEstComplete({
    dateIsSelected,
    heureIsSelected: heureIsSlected,
    aChasse,
    secteurs,
    allSecteurs,
  });

  const isPageLoading = load || dataLoading;

  return (
    <FermerEnregistrementFormBase
      texts={TEXTS}
      goTo={goTo}
      location={location}
      isLoading={isPageLoading}
      loadError={loadError}
      enregistrementEntete={enregistrementEntete}
      dataLoading={dataLoading}
      calendarRef={calendarRef}
      date={date}
      isOpenCalendar={isOpenCalendar}
      setIsOpenCalendar={setIsOpenCalendar}
      setdateIsSelected={setdateIsSelected}
      minSelectableDate={minSelectableDate}
      maxSelectableDate={maxSelectableDate}
      dateErrorKey={dateErrorKey}
      handleDateChange={handleDateChange}
      dateIsSelected={dateIsSelected}
      handleTimeChange={handleTimeChange}
    >
      {showHuntingQuestion &&
        <OuiNonToggle
          texts={TEXTS}
          label={TEXTS.huntedQuestion}
          value={aChasse}
          onChange={handleAChasseChange}
        />
      }

      {aChasse === true && secteurs.map((block, index) => {
        if (!shouldRenderSecteurBlock(block, index, allSecteurs, secteurs)) {
          return null;
        }

        return (
          <SecteurSection
            key={block.id}
            texts={TEXTS}
            index={index}
            block={block}
            isFirst={index === 0}
            showAutreSecteur={afficherAutreSecteur(index, secteurs, allSecteurs)}
            secteursDisponibles={getSecteursDisponibles(allSecteurs, secteurs, index)}
            onSecteurChange={handleSecteurChange}
            onNombrePersonnesChange={handleNombrePersonnesChange}
            onNombreJoursChange={handleNombreJoursChange}
            onPrisesChange={handlePrisesChange}
            onCaptureDetailChange={handleCaptureDetailChange}
            onAutreGibierChange={handleAutreGibierChange}
            onGibierChange={handleGibierChange}
            onAutreSecteurChange={handleAutreSecteurChange}
          />
        );
      })}

      {showSubmit &&
        <>
          {aChasse === true &&
            <CaptureVerificationSommaire
              texts={TEXTS}
              mode="chasse"
              blocks={secteurs}
              allLocations={allSecteurs}
            />
          }
          <div className="fec-submit-instruction">
            <span className="droit-acces-span">
              {TEXTS.submitInstruction}
            </span>
          </div>
          <button
            onClick={() => navigation()}
            className="fermer-enregistrement-button"
          >
            <span className="droit-acces-span">{TEXTS.closeButton}</span>
          </button>
        </>
      }
    </FermerEnregistrementFormBase>
  );
};

export default withNavigation(FermerEnregistrementChasseCapture);
